import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from "react-router-dom";

import {
	getDurationDateString,
	makeMeetingDateString,
	useClassNames,
	highlightText,
	calcMeetingRecordTime,
} from '@common';

import {
	MeetingStatus,
	DefaultTr,
	DefaultTd,
	IconButton,
	Space,
} from '@components';

import {
	MeetingListItem, MeetingStatuses, RecorderStatusEnum,
} from '@api';

import {
	routes,
} from '@routes';
import { MeetingPauseRecIcon, MeetingRecIcon, MeetingRecSettingsIcon, MeetingStopRecIcon,
	DeleteIcon,
 } from '@icons';
import { TaskLinkComp } from '../../meeting/summary/task-link';
import { SpeakerTranscriptResults } from './SpeakerTranscriptResults';

enum MeetingAction {
	'record',
	'stop_recording',
	'pause_recording',
	'resume_recording',
	'record_settings',
	'recording_info',
	'delete'
}

interface MeetingListTableTrProps {
	meeting: MeetingListItem,
	allowClickInProcess: boolean,
	isHighlighted: boolean,
	visited: boolean,
	onMeetingAction: (meeting: MeetingListItem, action: MeetingAction) => void;
}

const highlightRe = /<b>([^<]*)<\/b>/g;

function MeetingListTableTr(props: MeetingListTableTrProps): JSX.Element {
	const {
			meeting,
			allowClickInProcess,
			onMeetingAction,
			isHighlighted,
			visited,
		} = props;

	const
		history = useHistory(),
		dateText = makeMeetingDateString(meeting.startUtc, meeting.endUtc),
		durationText = getDurationDateString(meeting.recordingStartUtc, meeting.recordingEndUtc);

	const [disableActions, setDisableActions] = useState<boolean>(false);

	useEffect(() => {
		setDisableActions(false);
	}, [meeting]);

	const isMeetingInProcessing = useMemo(() => {
		return meeting.status.alias === MeetingStatuses.Processing;
	}, [meeting.status.alias]);

	const isMeetingInPlanned = useMemo(() => {
		return meeting.status.alias === MeetingStatuses.Plan;
	}, [meeting.status.alias]);

	const canMeetingBeDeleted = useMemo(() => {
		return meeting.status.alias === MeetingStatuses.Plan || meeting.status.alias === MeetingStatuses.Record;
	}, [meeting.status.alias]);

	const canShowDuration = useMemo(() => {
		return meeting.status.alias !== MeetingStatuses.Plan && meeting.status.alias !== MeetingStatuses.Record;
	}, [meeting.status.alias]);

	const isMeetingInPlannedAndReady = useMemo(() => {
		return meeting.status.alias === MeetingStatuses.Plan && meeting.recorderStatus == RecorderStatusEnum.Ready;
	}, [meeting.status.alias, meeting.recorderStatus]);

	const isMeetingInRecord = useMemo(() => {
		return meeting.status.alias === MeetingStatuses.Record && meeting.recorderStatus == RecorderStatusEnum.Recording;
	}, [meeting.status.alias, meeting.recorderStatus]);

	const isMeetingInPaused = useMemo(() => {
		return meeting.status.alias === MeetingStatuses.Record && meeting.recorderStatus == RecorderStatusEnum.Paused;
	}, [meeting.status.alias, meeting.recorderStatus]);

	const [timeElapsed, setTimeElapsed] = useState<string>(calcMeetingRecordTime(meeting.recordedDurationMs, meeting.lastRecordingSpanStartUtc, isMeetingInPaused));

    const intevalId = useRef(null);

	useEffect(() => {
        setDisableActions(false);

        const onTick = () => {
            setTimeElapsed(calcMeetingRecordTime(meeting.recordedDurationMs, meeting.lastRecordingSpanStartUtc, isMeetingInPaused));
        }

        if (meeting.recorderStatus == RecorderStatusEnum.Recording) {
            intevalId.current = setInterval(onTick, 1000);
        }

		return () => {
            if (intevalId.current) {
                clearInterval(intevalId.current);
            }
        }
	}, [meeting, isMeetingInPaused]);

	const classes = useClassNames(
		isMeetingInProcessing && !allowClickInProcess && '-processing',
		isHighlighted && '-highlighted',
		visited && '-visited',
		`row-${meeting.status.alias.toLowerCase()}`,
	);

	const onMeetingClick = useCallback((): void => {
		if (!allowClickInProcess) {
			if (meeting.isOwn) {
				if (meeting.status.alias === MeetingStatuses.Plan) {
					onMeetingAction(meeting, MeetingAction.record_settings);
					return;

				} else if (meeting.status.alias === MeetingStatuses.Record) {
					onMeetingAction(meeting, MeetingAction.recording_info);
					return;
				}
			} else if (meeting.status.alias === MeetingStatuses.Plan || meeting.status.alias === MeetingStatuses.Record) {
				return;
			}
		}

		history.push(routes.meetingPage.getPath(meeting.id));
	}, [meeting, history, allowClickInProcess, onMeetingAction]);

	

	const meetingSubject = useMemo(() => {
		return highlightText(meeting.subject, highlightRe);	
	}, [meeting.subject]);

	const onActionIconClick = useCallback((ev: React.MouseEvent, action: MeetingAction) => {
		ev.preventDefault();
		ev.stopPropagation();

		if (intevalId.current) {
            clearInterval(intevalId.current);
        }

		setDisableActions(true);
		onMeetingAction(meeting, action);
	}, [onMeetingAction, meeting]);

	const tasks = useMemo(() => {
		const res = meeting && meeting.tasks && meeting.tasks.map((t, ix) => <TaskLinkComp key={ix} taskLink={t} />);
		return res;
	}, [meeting]);

	const transcripts = useMemo(() => {
		if (!meeting || !meeting.transcriptSearchResult) {
			return null;
		}

		const speakerRows = meeting.transcriptSearchResult['speakers'].map((speakerTrans, ix) => {
			const speaker = speakerTrans['speaker'];
			const rows = speakerTrans['rows'].filter(r => r['Text'].includes("<b>"));

			return rows.length > 0 && <SpeakerTranscriptResults 
				key={ix}
				speakerName={speaker['name']}
				rows={rows}
			/>
		});

		return speakerRows;
	}, [meeting]);

	return (
		<DefaultTr onClick={onMeetingClick} className={classes}>
			<DefaultTd>
				<div className='meeting_subject'>
					<div className='meeting_subject_text'>
						<div>{meetingSubject}</div>
						<div className='additionalInfo'>{meeting.organizationName} {tasks}</div>
						<div className='transcript_result'>{transcripts}</div>						
					</div>
					<Space className='action_buttons'>
						{isMeetingInPlanned && meeting.isOwn ? <>
							<IconButton
								disabled={disableActions || !isMeetingInPlannedAndReady}
								icon={<MeetingRecIcon />} 
								iconSize="24px" 
								size="nogap" 
								onClick={(e) => onActionIconClick(e, MeetingAction.record)}
							/>
							<IconButton
								icon={<MeetingRecSettingsIcon />}
								iconSize="24px"
								size="nogap" 
								onClick={(e) => onActionIconClick(e, MeetingAction.record_settings)}
							/>
							</>
						: null }
						{isMeetingInRecord && meeting.isOwn ? <>
							<IconButton 
								disabled={disableActions}
								icon={<MeetingPauseRecIcon />}
								iconSize="24px"
								size="nogap"
								onClick={(e) => onActionIconClick(e, MeetingAction.pause_recording)}
							/>
							<IconButton
								disabled={disableActions} 
								icon={<MeetingStopRecIcon />}
								iconSize="24px"
								size="nogap" 
								onClick={(e) => onActionIconClick(e, MeetingAction.stop_recording)}
							/>
						</>
						: null }
						{isMeetingInPaused && meeting.isOwn ? <>
							<IconButton
								disabled={disableActions}
								icon={<MeetingRecIcon />}
								iconSize="24px"
								size="nogap"
								onClick={(e) => onActionIconClick(e, MeetingAction.resume_recording)}
							/>
							<IconButton
								disabled={disableActions}
								icon={<MeetingStopRecIcon />}
								iconSize="24px"
								size="nogap" 
								onClick={(e) => onActionIconClick(e, MeetingAction.stop_recording)}
							/>
						</>
						: null }
					</Space>
				</div>
			</DefaultTd>
			<DefaultTd>
				{dateText}
			</DefaultTd>
			<DefaultTd>
			{canShowDuration ?
				<>{durationText}</>
				: null }
			</DefaultTd>
			<DefaultTd>
				<Space className='status_area'>
					<MeetingStatus status={meeting.status} />
					{isMeetingInRecord || isMeetingInPaused ?
						<div className='meeting_recording_time'>{timeElapsed}</div>
						:null
					}
					{canMeetingBeDeleted && meeting.isOwn ?
						<IconButton
							className='meeting_delete'
							icon={<DeleteIcon />}
							iconSize="24px"
							size="nogap"
							onClick={(e) => onActionIconClick(e, MeetingAction.delete)}
						/>
					: null
					}
				</Space>
			</DefaultTd>
		</DefaultTr>
	);
}

export {MeetingListTableTr, MeetingAction};